// Overhead Allocation — Rules Master (MA-R2, Feb 2026)
//
// Screen 1 of 2 for the new Overhead Allocation Engine. Rules bind one
// Internal-Cost / Corporate PROJECT (source of indirect cost) to a set of
// Revenue PROJECTS via a chosen allocation BASIS
// (Fixed %, Revenue %, Direct Cost %, Headcount %, Manual).
//
// Screen 2 (Basis Snapshots) computes and freezes the per-period weights
// per rule × period. MA-R3 will introduce the Allocation Run.
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import {
  Plus, X, Pencil, Archive, Play, Pause, Search, ClipboardList,
  History, AlertCircle, Check,
} from "lucide-react";
import { toast, Toaster } from "sonner";

const STATUS_CHIPS = {
  Draft:    "bg-slate-100 text-slate-700 border-slate-300",
  Active:   "bg-emerald-50 text-emerald-700 border-emerald-300",
  Inactive: "bg-amber-50 text-amber-700 border-amber-300",
  Archived: "bg-slate-200 text-slate-500 border-slate-300 line-through",
};

const BASIS_ICON = {
  "Fixed %":       "🔒",
  "Revenue %":     "📈",
  "Direct Cost %": "💰",
  "Headcount %":   "👥",
  "Manual":        "✋",
};

export default function AllocationRulesPage() {
  const [facets, setFacets] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState({ status: "", basis_type: "", search: "" });
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);            // rule being edited
  const [showForm, setShowForm] = useState(false);
  const [showAudit, setShowAudit] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  async function loadFacets() {
    try {
      const { data } = await api.get("/mca/allocation-rules/facets");
      setFacets(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load facets");
    }
  }

  async function load() {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.status)     q.set("status", filter.status);
      if (filter.basis_type) q.set("basis_type", filter.basis_type);
      if (filter.search)     q.set("search", filter.search.trim());
      const { data } = await api.get(`/mca/allocation-rules?${q.toString()}`);
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load rules");
    } finally { setLoading(false); }
  }

  useEffect(() => { loadFacets(); }, []);
  useEffect(() => { load(); }, [filter.status, filter.basis_type]);

  async function activate(row) {
    if (!window.confirm(`Activate rule "${row.code} · ${row.name}"?`)) return;
    try {
      await api.post(`/mca/allocation-rules/${row.id}/activate`);
      toast.success("Rule activated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activate failed");
    }
  }
  async function deactivate(row) {
    if (!window.confirm(`Deactivate rule "${row.code}"? No new snapshots can be computed until it is reactivated.`)) return;
    try {
      await api.post(`/mca/allocation-rules/${row.id}/deactivate`);
      toast.success("Rule deactivated");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Deactivate failed");
    }
  }
  async function archive(row) {
    if (!window.confirm(`Archive rule "${row.code}"? Archived rules cannot be reactivated.`)) return;
    try {
      await api.post(`/mca/allocation-rules/${row.id}/archive`);
      toast.success("Rule archived");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archive failed");
    }
  }
  async function openAudit(row) {
    try {
      const { data } = await api.get(`/mca/allocation-rules/${row.id}/audit`);
      setAuditRows(data.rows || []);
      setShowAudit(row);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit fetch failed");
    }
  }

  const canEdit = (r) => ["Draft", "Inactive"].includes(r.status);

  return (
    <div data-testid="allocation-rules-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Overhead Allocation · MA-R2"
        title="Allocation Rules Master"
        subtitle="Each rule binds one Internal-Cost / Corporate source project to a set of Revenue Projects via a chosen basis. Rules pass through Draft → Active → Inactive / Archived. Only Active rules produce Basis Snapshots and Allocation Lines."
        actions={
          <button data-testid="allocation-rule-add-btn"
            onClick={() => { setEditing({ status: "Draft", target_selection_mode: "explicit", targets: [] }); setShowForm(true); }}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Plus size={14} /> New Rule
          </button>
        }
      />

      {/* Implementation-phase notice: shown while no Internal/Corporate
          projects exist. Informational, NOT a validation error. */}
      {facets && facets.source_projects.length === 0 && rows.length === 0 && (
        <Card className="mb-4 p-4 border-sky-200 bg-sky-50" data-testid="allocation-rules-empty-info">
          <div className="flex gap-3">
            <AlertCircle size={18} className="text-sky-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-sm font-bold text-sky-900 mb-1">
                Overhead Allocation is ready — awaiting Finance master-data setup.
              </div>
              <div className="text-[13px] text-sky-800 leading-relaxed">
                All existing projects are currently classified as <b>Revenue Projects</b>. This is
                the expected implementation-phase state. Overhead Allocation will become active once
                Finance reclassifies the required projects as <b>Internal Cost Project</b> or
                <b> Corporate Project</b> from Project Master. Rules, Snapshots, Runs and the
                Register are all ready to accept data as soon as reclassification is done.
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-4 p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px] relative">
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Search</label>
          <Search size={14} className="absolute left-3 bottom-3 text-slate-400" />
          <input value={filter.search}
            onChange={e => setFilter({ ...filter, search: e.target.value })}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder="Code, name, source project…"
            className="w-full pl-9 pr-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none"
            data-testid="allocation-rules-search" />
        </div>
        <div className="min-w-[160px]">
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Status</label>
          <select value={filter.status} onChange={e => setFilter({ ...filter, status: e.target.value })}
            className="w-full px-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none"
            data-testid="allocation-rules-status-filter">
            <option value="">All</option>
            <option value="Draft">Draft</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Archived">Archived</option>
          </select>
        </div>
        <div className="min-w-[180px]">
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Basis</label>
          <select value={filter.basis_type} onChange={e => setFilter({ ...filter, basis_type: e.target.value })}
            className="w-full px-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none"
            data-testid="allocation-rules-basis-filter">
            <option value="">All</option>
            {(facets?.basis_types || []).map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
        <button onClick={load} className="px-4 py-2.5 border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs uppercase tracking-wider font-bold"
          data-testid="allocation-rules-apply-filters">Apply</button>
      </Card>

      {/* List */}
      <Card>
        <div className="p-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500">
            {loading ? "Loading…" : `${total} rule${total === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="allocation-rules-table">
            <thead className="bg-slate-50">
              <tr className="text-left">
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Source Project</Th>
                <Th>Basis</Th>
                <Th>Targets</Th>
                <Th>Validity</Th>
                <Th>Status</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan="8" className="p-6 text-center text-slate-400 text-sm">
                  No rules yet. Create the first Overhead Allocation Rule to start allocating Internal / Corporate costs to Revenue Projects.
                </td></tr>
              )}
              {rows.map(r => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <Td mono>{r.code}</Td>
                  <Td>
                    <div className="font-medium text-slate-800">{r.name}</div>
                    {r.description && <div className="text-[11px] text-slate-500 line-clamp-1">{r.description}</div>}
                  </Td>
                  <Td>
                    <div className="text-[13px]">{r.source_project_code}</div>
                    <div className="text-[11px] text-slate-500">{r.source_project_name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">{r.source_project_group_name}</div>
                  </Td>
                  <Td>
                    <span className="inline-flex items-center gap-1 text-[12px]">
                      <span>{BASIS_ICON[r.basis_type] || ""}</span> {r.basis_type}
                    </span>
                  </Td>
                  <Td>
                    <div className="text-[12px] text-slate-700">
                      {r.target_selection_mode === "explicit"
                        ? `${(r.targets || []).length} explicit`
                        : "All Active Revenue"}
                    </div>
                    {r.target_selection_mode === "explicit" && r.basis_type === "Fixed %" && (
                      <div className="text-[10px] text-slate-500">
                        Σ = {((r.targets || []).reduce((s, t) => s + (Number(t.weight_percent) || 0), 0)).toFixed(2)}%
                      </div>
                    )}
                  </Td>
                  <Td>
                    <div className="text-[12px]">{r.valid_from}</div>
                    <div className="text-[11px] text-slate-500">{r.valid_to || "open"}</div>
                  </Td>
                  <Td>
                    <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold border ${STATUS_CHIPS[r.status] || ""}`}
                      data-testid={`allocation-rule-status-${r.code}`}>
                      {r.status}
                    </span>
                  </Td>
                  <Td className="text-right pr-2">
                    <div className="flex items-center justify-end gap-1">
                      {canEdit(r) && (
                        <button onClick={() => { setEditing(r); setShowForm(true); }}
                          className="p-1.5 hover:bg-slate-100 text-slate-600" title="Edit"
                          data-testid={`allocation-rule-edit-${r.code}`}><Pencil size={14} /></button>
                      )}
                      {r.status === "Draft" || r.status === "Inactive" ? (
                        <button onClick={() => activate(r)} className="p-1.5 hover:bg-emerald-50 text-emerald-700" title="Activate"
                          data-testid={`allocation-rule-activate-${r.code}`}><Play size={14} /></button>
                      ) : r.status === "Active" ? (
                        <button onClick={() => deactivate(r)} className="p-1.5 hover:bg-amber-50 text-amber-700" title="Deactivate"
                          data-testid={`allocation-rule-deactivate-${r.code}`}><Pause size={14} /></button>
                      ) : null}
                      {r.status !== "Active" && r.status !== "Archived" && (
                        <button onClick={() => archive(r)} className="p-1.5 hover:bg-slate-100 text-slate-500" title="Archive"
                          data-testid={`allocation-rule-archive-${r.code}`}><Archive size={14} /></button>
                      )}
                      <button onClick={() => openAudit(r)} className="p-1.5 hover:bg-slate-100 text-slate-500" title="Audit log"
                        data-testid={`allocation-rule-audit-${r.code}`}><History size={14} /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Editor drawer */}
      {showForm && facets && (
        <RuleEditor
          rule={editing}
          facets={facets}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {/* Audit drawer */}
      {showAudit && (
        <AuditDrawer rows={auditRows} rule={showAudit} onClose={() => setShowAudit(null)} />
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 ${className}`}>{children}</th>
);
const Td = ({ children, mono = false, className = "" }) => (
  <td className={`px-3 py-2.5 ${mono ? "font-mono text-[12px]" : ""} ${className}`}>{children}</td>
);

// ─────────────────────────────────────────────────────────────────────
// Rule Editor Drawer
// ─────────────────────────────────────────────────────────────────────
function RuleEditor({ rule, facets, onClose, onSaved }) {
  const isNew = !rule?.id;
  const [form, setForm] = useState(() => ({
    id: rule?.id,
    code: rule?.code || "",
    name: rule?.name || "",
    description: rule?.description || "",
    source_project_id: rule?.source_project_id || "",
    basis_type: rule?.basis_type || "Fixed %",
    target_selection_mode: rule?.target_selection_mode || "explicit",
    target_filter_branch_id: rule?.target_filter_branch_id || "",
    target_filter_business_vertical_id: rule?.target_filter_business_vertical_id || "",
    valid_from: rule?.valid_from || "",
    valid_to: rule?.valid_to || "",
    targets: (rule?.targets || []).map(t => ({ ...t })),
  }));
  const [saving, setSaving] = useState(false);

  const isFixed  = form.basis_type === "Fixed %";
  const isExplicit = form.target_selection_mode === "explicit";
  const totalWeight = useMemo(
    () => form.targets.reduce((s, t) => s + (Number(t.weight_percent) || 0), 0),
    [form.targets],
  );

  const addTarget = () => setForm({ ...form, targets: [...form.targets, { target_project_id: "", weight_percent: 0 }] });
  const rmTarget = (i) => setForm({ ...form, targets: form.targets.filter((_, idx) => idx !== i) });
  const updTarget = (i, patch) => setForm({
    ...form, targets: form.targets.map((t, idx) => (idx === i ? { ...t, ...patch } : t)),
  });

  async function save() {
    if (!form.name.trim())          { toast.error("Rule name is required."); return; }
    if (!form.source_project_id)    { toast.error("Source project is required."); return; }
    if (!form.valid_from)           { toast.error("valid_from is required (YYYY-MM)."); return; }
    if (isFixed && isExplicit) {
      const delta = Math.abs(totalWeight - 100);
      if (delta > 0.01) { toast.error(`Fixed % targets must sum to 100 (current ${totalWeight.toFixed(2)}%).`); return; }
    }
    if (isExplicit && form.targets.length === 0) {
      toast.error("Add at least one target project."); return;
    }
    // Enrich targets with codes/names from facets so the persisted list is denormalised
    const targetsPayload = form.targets.map(t => {
      const p = facets.target_projects.find(x => x.id === t.target_project_id) || {};
      return {
        target_project_id:   t.target_project_id,
        target_project_code: p.project_code || "",
        target_project_name: p.project_name || "",
        target_branch_id:    p.branch_id || "",
        target_branch_name:  p.branch_name || "",
        weight_percent:      Number(t.weight_percent) || 0,
      };
    });
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      source_project_id: form.source_project_id,
      basis_type: form.basis_type,
      target_selection_mode: form.target_selection_mode,
      target_filter_branch_id: form.target_filter_branch_id,
      target_filter_business_vertical_id: form.target_filter_business_vertical_id,
      valid_from: form.valid_from,
      valid_to: form.valid_to || "",
      targets: isExplicit ? targetsPayload : [],
    };
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/mca/allocation-rules", payload);
        toast.success("Rule created (Draft)");
      } else {
        await api.put(`/mca/allocation-rules/${form.id}`, payload);
        toast.success("Rule updated");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="allocation-rule-editor" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Overhead Allocation · MA-R2</div>
            <div className="text-lg font-bold text-slate-800">{isNew ? "New Allocation Rule" : `Edit · ${form.code}`}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100" data-testid="allocation-rule-editor-close"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Identity */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Code">
              <input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
                placeholder="Auto (AR-###) if blank" disabled={!isNew}
                className={inputCls + (isNew ? "" : " bg-slate-50 text-slate-500")}
                data-testid="allocation-rule-code" />
            </Field>
            <Field label="Rule Name *">
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                className={inputCls} data-testid="allocation-rule-name" />
            </Field>
          </div>
          <Field label="Description">
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              rows={2} className={inputCls} data-testid="allocation-rule-desc" />
          </Field>

          {/* Source */}
          <div className="border-t pt-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">Source · Internal / Corporate Project</div>
            <Field label="Source Project *">
              <select value={form.source_project_id}
                onChange={e => setForm({ ...form, source_project_id: e.target.value })}
                className={inputCls} data-testid="allocation-rule-source">
                <option value="">— Choose Internal / Corporate project —</option>
                {facets.source_projects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.project_code} · {p.project_name} ({p.project_group_name})
                  </option>
                ))}
              </select>
            </Field>
            {facets.source_projects.length === 0 && (
              <div className="mt-2 text-[11px] text-sky-800 bg-sky-50 border border-sky-200 p-3">
                <div className="flex gap-2">
                  <AlertCircle size={12} className="mt-0.5 flex-shrink-0 text-sky-600" />
                  <div>
                    <div className="font-bold text-sky-900 mb-0.5">No source projects yet — this is expected during implementation.</div>
                    All existing projects are currently classified as <b>Revenue Projects</b>. Overhead Allocation becomes available once Finance reclassifies the required projects as <b>Internal Cost Project</b> or <b>Corporate Project</b> from the Project Master.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Basis */}
          <div className="border-t pt-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">Allocation Basis</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Basis Type *">
                <select value={form.basis_type} onChange={e => setForm({ ...form, basis_type: e.target.value })}
                  className={inputCls} data-testid="allocation-rule-basis">
                  {facets.basis_types.map(b => <option key={b} value={b}>{BASIS_ICON[b]} {b}</option>)}
                </select>
              </Field>
              <Field label="Target Mode *">
                <select value={form.target_selection_mode}
                  onChange={e => setForm({ ...form, target_selection_mode: e.target.value })}
                  className={inputCls} data-testid="allocation-rule-target-mode"
                  disabled={isFixed}>
                  <option value="explicit">Explicit list</option>
                  <option value="all_active_revenue">All Active Revenue Projects</option>
                </select>
              </Field>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              {form.basis_type === "Fixed %" && "Fixed % requires an explicit target list summing to exactly 100%."}
              {form.basis_type === "Revenue %" && "Snapshot weights are computed from each target's Net Revenue in the period."}
              {form.basis_type === "Direct Cost %" && "Snapshot weights are computed from each target's Direct Cost (CE-R1a) in the period."}
              {form.basis_type === "Headcount %" && "Snapshot weights are computed from approved Salary Payment line counts per target project."}
              {form.basis_type === "Manual" && "You will enter each target's weight manually on every period's snapshot before freezing."}
            </div>
          </div>

          {/* Auto-mode filters */}
          {!isExplicit && (
            <div className="grid grid-cols-2 gap-4">
              <Field label="Branch filter (optional)">
                <select value={form.target_filter_branch_id}
                  onChange={e => setForm({ ...form, target_filter_branch_id: e.target.value })}
                  className={inputCls} data-testid="allocation-rule-auto-branch">
                  <option value="">— all branches —</option>
                  {facets.branches.map(b => <option key={b.id} value={b.id}>{b.branch_code} · {b.branch_name}</option>)}
                </select>
              </Field>
              <Field label="Business Vertical filter (optional)">
                <select value={form.target_filter_business_vertical_id}
                  onChange={e => setForm({ ...form, target_filter_business_vertical_id: e.target.value })}
                  className={inputCls} data-testid="allocation-rule-auto-vertical">
                  <option value="">— all verticals —</option>
                  {facets.business_verticals.map(v => <option key={v.id} value={v.id}>{v.code} · {v.name}</option>)}
                </select>
              </Field>
            </div>
          )}

          {/* Validity */}
          <div className="border-t pt-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mb-3">Validity Window (YYYY-MM)</div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Valid From *">
                <input type="month" value={form.valid_from}
                  onChange={e => setForm({ ...form, valid_from: e.target.value })}
                  className={inputCls} data-testid="allocation-rule-valid-from" />
              </Field>
              <Field label="Valid To (blank = open)">
                <input type="month" value={form.valid_to}
                  onChange={e => setForm({ ...form, valid_to: e.target.value })}
                  className={inputCls} data-testid="allocation-rule-valid-to" />
              </Field>
            </div>
          </div>

          {/* Explicit targets */}
          {isExplicit && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">
                  Target Revenue Projects
                </div>
                <div className="flex items-center gap-3">
                  {isFixed && (
                    <div className={`text-[12px] font-bold ${Math.abs(totalWeight - 100) <= 0.01 ? "text-emerald-600" : "text-red-600"}`}
                      data-testid="allocation-rule-total-weight">
                      Σ {totalWeight.toFixed(2)}% {Math.abs(totalWeight - 100) <= 0.01 ? "✓" : "→ must equal 100.00"}
                    </div>
                  )}
                  <button onClick={addTarget}
                    className="text-[11px] uppercase tracking-wider font-bold text-[#F97316] hover:text-[#EA580C] flex items-center gap-1"
                    data-testid="allocation-rule-add-target"><Plus size={12} /> Add target</button>
                </div>
              </div>
              {form.targets.length === 0 && (
                <div className="text-center text-slate-400 text-xs py-4 border border-dashed border-slate-300">
                  No target projects yet. Add at least one Revenue Project.
                </div>
              )}
              <div className="space-y-2">
                {form.targets.map((t, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`allocation-rule-target-row-${i}`}>
                    <select value={t.target_project_id}
                      onChange={e => updTarget(i, { target_project_id: e.target.value })}
                      className={inputCls + " flex-1"}
                      data-testid={`allocation-rule-target-${i}-project`}>
                      <option value="">— Revenue Project —</option>
                      {facets.target_projects.map(p => (
                        <option key={p.id} value={p.id}>{p.project_code} · {p.project_name}</option>
                      ))}
                    </select>
                    <div className="w-40">
                      <div className="flex items-center gap-1">
                        <input type="number" min="0" max="100" step="0.01" value={t.weight_percent}
                          onChange={e => updTarget(i, { weight_percent: e.target.value })}
                          className={inputCls + (isFixed ? "" : " bg-slate-50")}
                          disabled={!isFixed}
                          data-testid={`allocation-rule-target-${i}-weight`} />
                        <span className="text-slate-500 text-sm">%</span>
                      </div>
                      {!isFixed && (
                        <div className="text-[10px] text-slate-400">(auto per period)</div>
                      )}
                    </div>
                    <button onClick={() => rmTarget(i)}
                      className="p-2 hover:bg-red-50 text-red-600"
                      data-testid={`allocation-rule-target-${i}-remove`}><X size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between">
          <div className="text-[11px] text-slate-500">Saved as <b>Draft</b>. Activate from the list to unlock snapshot computation.</div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs uppercase tracking-wider font-bold"
              data-testid="allocation-rule-cancel">Cancel</button>
            <button onClick={save} disabled={saving}
              className="px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs uppercase tracking-wider font-bold disabled:opacity-50"
              data-testid="allocation-rule-save">{saving ? "Saving…" : (isNew ? "Create Draft" : "Save Changes")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Audit drawer
// ─────────────────────────────────────────────────────────────────────
function AuditDrawer({ rows, rule, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" data-testid="allocation-rule-audit" onClick={onClose}>
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Audit Trail</div>
            <div className="text-lg font-bold text-slate-800">{rule.code} · {rule.name}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-2">
          {rows.length === 0 && (
            <div className="text-slate-400 text-sm text-center py-8">No audit events yet.</div>
          )}
          {rows.map(r => (
            <div key={r.id} className="border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between text-[11px] text-slate-500">
                <div className="font-mono">{new Date(r.timestamp).toLocaleString()}</div>
                <div>{r.user_email} <span className="text-slate-400">· {r.user_role}</span></div>
              </div>
              <div className="mt-1 font-bold text-slate-800 text-sm flex items-center gap-2">
                {r.outcome === "success" ? <Check size={14} className="text-emerald-600" /> : <AlertCircle size={14} className="text-red-600" />}
                {r.event_type}
              </div>
              {r.reason && <div className="text-[12px] text-slate-600 mt-1">Reason: {r.reason}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Match the visual language used by CostCentres / Users pages.
const inputCls = "w-full px-3 py-2.5 border border-slate-300 text-sm bg-white focus:border-[#F97316] focus:outline-none";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);
